import { AfterViewInit, ChangeDetectorRef, Component, inject, Inject, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import { environment } from '@env/environment';
import {
  MbBaseComponent,
  MbLoadingBarService,
  MbLoadingBarComponent,
  MbBreadcrumbsComponent,
} from '@montblancsimpl/core';
import { TranslateService } from '@ngx-translate/core';
import { merge } from 'rxjs';
import { filter, map, mergeMap } from 'rxjs/operators';

import { I18nService } from './core/i18n.service';
import { Logger } from './core/logger.service';
import { AuthenticationService } from './core/authentication/authentication.service';
import { HeaderComponent } from './layouts/header/header.component';
import { MbCarouselService } from './shared/components/carousel/carousel.service';
import { NewsDto } from './news/@shared/models/NewsDto';
import { NewsCarouselComponent } from './shared/components/news-carousel/news-carousel.component';
import { CommonModule } from '@angular/common';
import { CoreModule } from '@app/core/core.module';

/**
 * This component represents the start of the application.
 * The setup of basic applications things, such as
 *
 *  - the logger,
 *  - i18n support,
 *  - browser title support,
 *  - and the navigation loading bar
 *
 * is handled here.
 *
 * @export
 * @class AppComponent
 * @implements {OnInit}
 */
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    MbLoadingBarComponent,
    MbBreadcrumbsComponent,
    CommonModule,
    HeaderComponent,
    RouterOutlet,
    NewsCarouselComponent,
  ],
})
export class AppComponent extends MbBaseComponent implements OnInit, AfterViewInit {
  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private translateService: TranslateService,
    private i18nService: I18nService,
    private loadingBarService: MbLoadingBarService,
    public carouselService: MbCarouselService<NewsDto>,
    private cdr: ChangeDetectorRef,
    public authenticationService: AuthenticationService,
  ) {
    super();
  }

  ngOnInit() {
    // Logger
    if (environment.production) {
      Logger.enableProductionMode();
    }

    // Loading bar on navigation
    this.router.events.subscribe((event: any): void => {
      this.navigationInterceptor(event);
    });

    // I18n support
    this.i18nService.init(
      environment.supportedLanguages.find((q) => q.locale === environment.defaultLanguage),
      environment.supportedLanguages,
    );

    // Browser title
    merge(
      this.translateService.onLangChange,
      this.router.events.pipe(filter((event) => event instanceof NavigationEnd)),
    )
      .pipe(
        map(() => {
          let route = this.activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data),
      )
      .subscribe((event) => {
        const title = event['title'];
        if (title) {
          this.titleService.setTitle(this.translateService.instant(title));
        }
      });
  }

  ngAfterViewInit(): void {
    merge(this.router.events.pipe(filter((event) => event instanceof NavigationStart))).subscribe(
      (event) => {
        this.carouselService.setVisibility(false);
      },
    );
    this.cdr.detectChanges();
  }

  /**
   * Intercepts navigation events and starts or stops the loading bar, depending
   * on the intercepted event
   *
   * @private
   * @param {*} event The intercepted event
   * @memberof AppComponent
   */
  private navigationInterceptor(event: any): void {
    if (event instanceof NavigationStart) {
      this.loadingBarService.start();
    }
    if (event instanceof NavigationEnd) {
      this.completeLoadingAfterTime();
    }
    if (event instanceof NavigationCancel) {
      this.completeLoadingAfterTime();
    }
    if (event instanceof NavigationError) {
      this.completeLoadingAfterTime();
    }
  }

  /**
   * Delays the completion of the loading bar by 200ms to merge multiple
   * loading bar calls such as backend calls after the routing finished
   *
   * @private
   * @memberof AppComponent
   */
  private completeLoadingAfterTime() {
    setTimeout(() => {
      if (this.loadingBarService.pendingRequests <= 3) {
        this.loadingBarService.complete();
      }
    }, 200);
  }
}
